import React from 'react';

import { ProjectedIncomeSummary } from '@ndvr/reporting-api';

import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/utils/cn';

import { WholeDollars } from '../../reporting/HeroStatCard';
import {
    HEAD_CLASS,
    HEAD_ROW_CLASS,
    MV_CELL,
    NUMERIC_CELL,
    RIGHT_HEAD_CLASS,
    TABLE_CARD_HEADER_CLASS,
    TABLE_CLASS,
} from '../../reporting/table-styles';
import { buildIncomeMatrix, INCOME_CATEGORY_LABELS } from '../income';

// Income projected by asset class over the current quarter plus three, with totals on both axes.
// Past its minimum width the table scrolls sideways (Table's own overflow) instead of squeezing the
// quarter columns.
export const ProjectedIncomeCard = ({ projectedIncome }: { projectedIncome: ProjectedIncomeSummary[] }) => {
    const matrix = buildIncomeMatrix(projectedIncome);

    return (
        <Card className="border-border overflow-hidden rounded-2xl border bg-white">
            <div className={TABLE_CARD_HEADER_CLASS}>
                <h3 className="font-heading text-cocoa-1000 text-xl">Projected income</h3>
            </div>
            <Table className={cn(TABLE_CLASS, 'min-w-[620px]')}>
                <TableHeader>
                    <TableRow className={HEAD_ROW_CLASS}>
                        <TableHead className={HEAD_CLASS}>Asset class</TableHead>
                        {matrix.quarters.map((quarter) => (
                            <TableHead key={quarter.periodStart} className={RIGHT_HEAD_CLASS}>
                                {quarter.label}
                            </TableHead>
                        ))}
                        <TableHead className={RIGHT_HEAD_CLASS}>Total</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {matrix.rows.map((row) => (
                        <TableRow key={row.category}>
                            <TableCell className="text-cocoa-1000 text-sm font-semibold">
                                {INCOME_CATEGORY_LABELS[row.category] ?? row.category}
                            </TableCell>
                            {row.amounts.map((amount, i) => (
                                <TableCell
                                    key={matrix.quarters[i].periodStart}
                                    className={cn(NUMERIC_CELL, 'text-cocoa-900')}
                                >
                                    <WholeDollars value={amount} />
                                </TableCell>
                            ))}
                            <TableCell className={MV_CELL}>
                                <WholeDollars value={row.total} />
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow className="bg-cocoa-100/40 hover:bg-cocoa-100/40">
                        <TableCell className="text-cocoa-1000 text-sm font-semibold">Total</TableCell>
                        {matrix.columnTotals.map((total, i) => (
                            <TableCell key={matrix.quarters[i].periodStart} className={MV_CELL}>
                                <WholeDollars value={total} />
                            </TableCell>
                        ))}
                        <TableCell className={MV_CELL}>
                            <WholeDollars value={matrix.grandTotal} />
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </Card>
    );
};
